import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Header } from '@/components/Header';
import { Footer } from '@/components/Footer';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card } from '@/components/ui/card';
import { useAuth } from '@/context/AuthContext';
import { toast } from 'sonner';
import axios from 'axios';
import { Loader2 } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const Login = () => {
  const [step, setStep] = useState(1);
  const [phone, setPhone] = useState('');
  const [otp, setOtp] = useState('');
  const [loading, setLoading] = useState(false);
  const { login } = useAuth();
  const navigate = useNavigate();

  const handleSendOTP = async (e) => {
    e.preventDefault();
    
    if (!phone || phone.length < 10) {
      toast.error('Please enter a valid phone number');
      return;
    }

    setLoading(true);
    try {
      const response = await axios.post(`${API}/auth/confirm-number`, { phone });
      login(response.data.access_token, response.data.user);
      toast.success('Login successful!');
      navigate('/');
      } catch (error) {
      toast.error('Failed to send OTP');
      } finally {
      setLoading(false);
    }
  };
    /*try {
      const response = await axios.post(`${API}/auth/send-otp`, { phone });
      toast.success('OTP sent successfully!');
      if (response.data.otp) {
        toast.info(`Your OTP is: ${response.data.otp}`);
      }
      setStep(2);
    } catch (error) {
      toast.error('Failed to send OTP');
    } finally {
      setLoading(false);
    }
  };*/

  const handleVerifyOTP = async (e) => {
    e.preventDefault();
    
    if (!otp || otp.length !== 6) {
      toast.error('Please enter a valid 6-digit OTP');
      return;
    }

    setLoading(true);
    try {
      const response = await axios.post(`${API}/auth/verify-otp`, {
        phone,
        code: otp
      });
      
      login(response.data.access_token, response.data.user);
      toast.success('Login successful!');
      navigate('/');
    } catch (error) {
      toast.error('Invalid OTP');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex flex-col">
      <Header />
      
      <div className="flex-1 bg-gradient-to-b from-amber-50 to-white flex items-center justify-center py-12 px-4">
        <Card className="w-full max-w-md p-8">
          <div className="text-center mb-8">
            <h1 className="text-3xl font-bold text-accent mb-2" data-testid="login-title">
              Welcome Back!
            </h1>
            <p className="text-muted-foreground">
              Login with your mobile number
            </p>
          </div>

          {step === 1 ? (
            <form onSubmit={handleSendOTP} className="space-y-4" data-testid="phone-form">
              <div>
                <Label htmlFor="phone">Mobile Number</Label>
                <Input
                  id="phone"
                  type="tel"
                  placeholder="+91XXXXXXXXXX"
                  value={phone}
                  onChange={(e) => setPhone(e.target.value)}
                  className="mt-1"
                  required
                  data-testid="phone-input"
                />
                <p className="text-xs text-muted-foreground mt-1">
                  Enter with country code (e.g., +919876543210)
                </p>
              </div>

              <Button
                type="submit"
                className="w-full h-12 rounded-full bg-primary hover:bg-primary/90 font-medium transition-all hover:scale-105 active:scale-95 shadow-lg"
                disabled={loading}
                data-testid="confirm-number" //"send-otp-button"
              >
                {loading ? (
                  <>
                    <Loader2 className="mr-2 h-5 w-5 animate-spin" />
                    Sending...
                  </>
                ) : (
                  //'Send OTP'
                  'Confirm Number'
                )}
              </Button>
            </form>
          ) : (
            <form onSubmit={handleVerifyOTP} className="space-y-4" data-testid="otp-form">
              <div>
                <Label htmlFor="otp">Enter OTP</Label>
                <Input
                  id="otp"
                  type="text"
                  placeholder="Enter 6-digit OTP"
                  value={otp}
                  onChange={(e) => setOtp(e.target.value)}
                  maxLength={6}
                  className="mt-1 text-center text-2xl tracking-widest"
                  required
                  data-testid="otp-input"
                />
                <p className="text-xs text-muted-foreground mt-1 text-center">
                  OTP sent to {phone}
                </p>
              </div>

              <div className="flex gap-2">
                <Button
                  type="button"
                  variant="outline"
                  className="flex-1 h-12 rounded-full"
                  onClick={() => {
                    setStep(1);
                    setOtp('');
                  }}
                  data-testid="change-number-button"
                >
                  Change Number
                </Button>
                <Button
                  type="submit"
                  className="flex-1 h-12 rounded-full bg-primary hover:bg-primary/90 font-medium transition-all hover:scale-105 active:scale-95 shadow-lg"
                  disabled={loading}
                  data-testid="verify-otp-button"
                >
                  {loading ? (
                    <>
                      <Loader2 className="mr-2 h-5 w-5 animate-spin" />
                      Verifying...
                    </>
                  ) : (
                    'Verify OTP'
                  )}
                </Button>
              </div>

              <Button
                type="button"
                variant="ghost"
                className="w-full"
                onClick={handleSendOTP}
                disabled={loading}
                data-testid="resend-otp-button"
              >
                Resend OTP
              </Button>
            </form>
          )}
        </Card>
      </div>

      <Footer />
    </div>
  );
};

export default Login;
